/* Sevkiyat Monitörü — Kiosk sayfası.

Public kiosk URL: /kiosk/picking?t=<kiosk_token>
- Barcode scanner keyboard emülasyonu ile "TRACKING\n" yazar → onEnter → scan API
- Polling her 3 sn (pending + stats), scan sonrası anında refresh
- Ses efekti: Web Audio API ile programatik beep (external file yok)
- SLA renkleri: <15dk yeşil, 15-60dk sarı, >60dk kırmızı
- Kart üstünde: Manuel Pick + Tekrar Bas butonları
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import {
    Package, MapPin, Truck, RefreshCw, CheckCircle2, Printer,
    ScanLine, Maximize2, Volume2, VolumeX, AlertTriangle, Loader2,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Ses efektleri (Web Audio API — file dependency yok) ───
const playBeep = (type = 'ok') => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        if (type === 'ok') {
            o.frequency.value = 880; g.gain.value = 0.25;
            o.start(); o.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.06);
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
            setTimeout(() => { o.stop(); ctx.close(); }, 130);
        } else if (type === 'err') {
            o.frequency.value = 220; g.gain.value = 0.25;
            o.type = 'square'; o.start();
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
            setTimeout(() => { o.stop(); ctx.close(); }, 450);
        } else if (type === 'warn') {
            o.frequency.value = 440; g.gain.value = 0.18;
            o.start();
            g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.22);
            setTimeout(() => { o.stop(); ctx.close(); }, 250);
        }
    } catch { /* audio not supported */ }
};

// ─── Kart bekleme süresi + SLA rengi (sade beyaz tema) ───
const slaFor = (orderDate) => {
    if (!orderDate) return { min: 0, cls: 'border-slate-200 bg-white', label: 'yeni' };
    const d = new Date(orderDate);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 15) return { min: diffMin, cls: 'border-emerald-300 bg-white' };
    if (diffMin < 60) return { min: diffMin, cls: 'border-amber-300 bg-amber-50/40' };
    return { min: diffMin, cls: 'border-rose-400 bg-rose-50/30' };
};

const fmtMin = (m) => {
    if (m < 60) return `${m}dk`;
    const h = Math.floor(m / 60);
    return `${h}s ${m % 60}dk`;
};

// Iter 113.34: Sipariş verilme tarihi (dd.MM HH:mm formatı — Türk operatörler için)
const fmtOrderDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 16).replace('T', ' ');
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const flagFor = (cc) => {
    const map = { NL: '🇳🇱', BE: '🇧🇪', DE: '🇩🇪', FR: '🇫🇷', LU: '🇱🇺', GB: '🇬🇧' };
    return map[cc] || '🌐';
};

export default function KioskPicking() {
    const [params] = useSearchParams();
    const token = params.get('t') || '';
    const hdr = useMemo(() => ({ 'X-Kiosk-Token': token }), [token]);

    const [pending, setPending] = useState([]);
    const [completed, setCompleted] = useState([]);
    const [stats, setStats] = useState({ pending: 0, picked_today: 0 });
    const [loading, setLoading] = useState(true);
    const [muted, setMuted] = useState(false);
    const [lastScan, setLastScan] = useState(null);   // { ok, order_id, at }
    const scanInputRef = useRef(null);
    const [scanValue, setScanValue] = useState('');
    const beep = useCallback((t) => { if (!muted) playBeep(t); }, [muted]);

    const load = useCallback(async () => {
        try {
            const [p, c, s] = await Promise.all([
                axios.get(`${API}/api/bol/picking/pending`, { headers: hdr }),
                axios.get(`${API}/api/bol/picking/completed`, { headers: hdr, params: { limit: 20 } }),
                axios.get(`${API}/api/bol/picking/stats`, { headers: hdr }),
            ]);
            setPending(p.data?.items || []);
            setCompleted(c.data?.items || []);
            setStats(s.data || { pending: 0, picked_today: 0 });
        } catch (e) {
            if (e.response?.status === 401) {
                toast.error('Kiosk token geçersiz');
            }
        } finally { setLoading(false); }
    }, [hdr]);

    // Polling 3 saniye
    useEffect(() => {
        if (!token) return;
        load();
        const iv = setInterval(load, 3000);
        return () => clearInterval(iv);
    }, [token, load]);

    // Barcode input sürekli focus
    useEffect(() => {
        const focus = () => scanInputRef.current?.focus();
        focus();
        const iv = setInterval(focus, 500);
        window.addEventListener('click', focus);
        return () => { clearInterval(iv); window.removeEventListener('click', focus); };
    }, []);

    const doScan = async (barcode) => {
        if (!barcode || barcode.length < 3) return;
        try {
            const r = await axios.post(`${API}/api/bol/picking/scan`,
                { barcode }, { headers: hdr });
            const d = r.data || {};
            if (d.ok) {
                beep('ok');
                setLastScan({ ok: true, order_id: d.order_id, at: Date.now() });
                toast.success(`✅ ${d.order_id} hazırlandı`, { duration: 2000 });
            } else if (d.reason === 'already_picked') {
                beep('warn');
                toast.warning(`⚠ ${d.order_id} zaten hazırlanmış`, { duration: 2500 });
            } else if (d.reason === 'not_found') {
                beep('err');
                toast.error(`✗ Barcode eşleşmedi: ${d.barcode}`, { duration: 2500 });
            } else if (d.reason === 'cancelled') {
                beep('err');
                toast.error(`✗ Sipariş iptal edilmiş`, { duration: 2500 });
            }
            load();
        } catch (e) {
            beep('err');
            toast.error(e.response?.data?.detail || 'Scan hatası');
        }
    };

    const manualPick = async (order_id) => {
        try {
            const r = await axios.post(`${API}/api/bol/picking/${order_id}/manual-pick`,
                {}, { headers: hdr });
            if (r.data?.ok) {
                beep('ok');
                toast.success(`✅ ${order_id} hazırlandı (manuel)`);
                setLastScan({ ok: true, order_id, at: Date.now() });
                load();
            } else {
                beep('warn');
                toast.warning('Zaten hazırlanmış');
            }
        } catch (e) {
            beep('err');
            toast.error(e.response?.data?.detail || 'Manuel pick hatası');
        }
    };

    const reprint = async (order_id) => {
        try {
            const r = await axios.post(`${API}/api/bol/picking/${order_id}/reprint`,
                {}, { headers: hdr });
            if (r.data?.ok) {
                toast.success(`🖨 Etiket tekrar basıldı (job #${r.data.job_id})`);
            }
        } catch (e) {
            beep('err');
            toast.error(e.response?.data?.detail || 'Etiket basılamadı');
        }
    };

    const enterFullscreen = () => {
        const el = document.documentElement;
        if (el.requestFullscreen) el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    };

    if (!token) {
        return (
            <div className="min-h-screen bg-white text-slate-900 flex items-center justify-center p-8">
                <div className="max-w-md text-center space-y-4">
                    <AlertTriangle size={48} className="text-amber-500 mx-auto" />
                    <h1 className="text-2xl font-bold">Kiosk Token Eksik</h1>
                    <p className="text-slate-600 text-sm">URL&apos;de <code className="text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">?t=&lt;kiosk_token&gt;</code> parametresi gerekli.
                    Owner Ayarlar sayfasından token oluşturun ve doğru URL ile açın.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 select-none" data-testid="kiosk-picking">
            <Toaster position="top-center" richColors closeButton={false} />
            {/* Görünmez barcode input — sürekli focus */}
            <input
                ref={scanInputRef}
                value={scanValue}
                onChange={(e) => setScanValue(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = scanValue.trim();
                        setScanValue('');
                        doScan(v);
                    }
                }}
                autoFocus
                data-testid="kiosk-barcode-input"
                className="fixed opacity-0 top-0 left-0 w-1 h-1 pointer-events-none"
            />

            {/* Header — beyaz sade */}
            <div className="bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10 shadow-sm">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Package size={26} className="text-slate-700" />
                        <div>
                            <h1 className="text-base font-bold tracking-wide text-slate-900">SEVKİYAT MONİTÖRÜ</h1>
                            <div className="text-[11px] text-slate-500">Etiketi barcode scanner ile tarayın</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-6">
                        <Stat n={stats.pending} l="Bekleyen" color="text-amber-600" testId="k-stat-pending" />
                        <Stat n={stats.picked_today} l="Bugün Hazır" color="text-emerald-600" testId="k-stat-picked" />
                        <div className="flex items-center gap-1 border-l border-slate-200 pl-3 ml-2">
                            <button onClick={() => setMuted((m) => !m)}
                                className="p-2 rounded hover:bg-slate-100 text-slate-600"
                                title={muted ? 'Sesi aç' : 'Sesi kapat'}
                                data-testid="k-mute-toggle">
                                {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                            </button>
                            <button onClick={enterFullscreen}
                                className="p-2 rounded hover:bg-slate-100 text-slate-600"
                                title="Tam ekran"
                                data-testid="k-fullscreen">
                                <Maximize2 size={18} />
                            </button>
                            <button onClick={load}
                                className="p-2 rounded hover:bg-slate-100 text-slate-600"
                                title="Yenile"
                                data-testid="k-refresh">
                                <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
                            </button>
                        </div>
                    </div>
                </div>
                {lastScan && (Date.now() - lastScan.at < 3000) && (
                    <div className="mt-2 bg-emerald-50 border border-emerald-300 rounded px-3 py-1.5 text-sm flex items-center gap-2 text-emerald-800">
                        <ScanLine size={14} className="text-emerald-600" />
                        <span className="font-mono">{lastScan.order_id}</span>
                        <span>✓ hazırlandı</span>
                    </div>
                )}
            </div>

            {/* Card grid — 3 kolon = 9 kart görünsün geniş */}
            <div className="p-4 pb-24">
                {loading && pending.length === 0 ? (
                    <div className="flex items-center justify-center py-24">
                        <Loader2 size={48} className="animate-spin text-slate-400" />
                    </div>
                ) : pending.length === 0 ? (
                    <div className="text-center py-24 text-slate-500">
                        <CheckCircle2 size={64} className="mx-auto mb-4 text-emerald-500" />
                        <div className="text-2xl font-bold text-slate-900 mb-1">Bekleyen sipariş yok</div>
                        <div className="text-sm">Tüm siparişler hazır. Yeni sipariş geldiğinde otomatik görünecek.</div>
                    </div>
                ) : (
                    <div className="grid grid-cols-3 gap-3">
                        {pending.slice(0, 9).map((c) => <PickCard
                            key={c.order_id}
                            c={c}
                            onManual={() => manualPick(c.order_id)}
                            onReprint={() => reprint(c.order_id)}
                        />)}
                    </div>
                )}
                {pending.length > 9 && (
                    <div className="text-center text-xs text-slate-500 mt-3">
                        + {pending.length - 9} sipariş daha kuyrukta (ilk 9 gösteriliyor)
                    </div>
                )}
            </div>

            {/* Alt şerit — bugün hazırlananlar (beyaz sade) */}
            {completed.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-200 px-6 py-2 overflow-hidden shadow-lg"
                    data-testid="k-completed-strip">
                    <div className="flex items-center gap-4 overflow-x-auto">
                        <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold flex items-center gap-1 shrink-0">
                            <CheckCircle2 size={13} /> Bugün Hazır ({completed.length})
                        </div>
                        {completed.map((d) => (
                            <div key={d.order_id} className="flex items-center gap-1.5 text-xs shrink-0 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                                <span>{flagFor(d.country)}</span>
                                <span className="font-mono text-emerald-800">#{d.order_id_short}</span>
                                <span className="text-emerald-600/70 text-[10px]">
                                    {d.picked_at ? new Date(d.picked_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ n, l, color, testId }) {
    return (
        <div className="text-right" data-testid={testId}>
            <div className={`text-3xl font-black ${color}`}>{n}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{l}</div>
        </div>
    );
}

function PickCard({ c, onManual, onReprint }) {
    const sla = slaFor(c.order_date);
    return (
        <div className={`rounded-lg border p-3 text-slate-900 shadow-sm ${sla.cls}`}
            data-testid={`k-card-${c.order_id}`}>
            {/* Iter 113.33: Alıcı adı büyük + göze çarpsın */}
            <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                    <div className="text-lg font-black text-slate-900 leading-tight truncate" title={c.customer_name}>
                        {c.customer_name || '—'}
                    </div>
                    <div className="flex items-center gap-1 text-[11px] text-slate-600 mt-0.5">
                        <span className="text-base">{flagFor(c.country)}</span>
                        <MapPin size={10} />
                        <span className="truncate">{c.city || '—'}</span>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Sipariş</div>
                    <div className="font-mono text-xs font-bold text-slate-700">#{c.order_id_short}</div>
                    {c.order_date && (
                        <div className="text-[10px] text-slate-500 font-mono mt-0.5" data-testid={`k-order-date-${c.order_id}`}>
                            📅 {fmtOrderDate(c.order_date)}
                        </div>
                    )}
                    <div className="text-[11px] font-semibold mt-0.5" style={{ color: sla.min >= 60 ? '#e11d48' : sla.min >= 15 ? '#d97706' : '#059669' }}>
                        ⏱ {fmtMin(sla.min)}
                    </div>
                </div>
            </div>

            {/* Iter 113.33: Track & Trace numarası BÜYÜK — barcode teyidi için */}
            {c.track_and_trace && (
                <div className="bg-slate-900 text-white rounded px-2 py-1.5 mb-2 text-center">
                    <div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">
                        {c.transporter_code || 'Kargo'} · Barcode
                    </div>
                    <div className="font-mono text-base font-bold tracking-wider mt-0.5">
                        {c.track_and_trace}
                    </div>
                </div>
            )}

            <div className="border-t border-slate-100 pt-1.5 mb-1.5">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1 mb-1">
                    <Package size={10} /> {c.line_count} kalem · {c.total_qty} adet
                </div>
                <div className="space-y-1 max-h-20 overflow-y-auto pr-1">
                    {(c.lines || []).map((ln, i) => (
                        <div key={i} className="text-[11px] bg-slate-50 rounded px-1.5 py-1 border border-slate-100">
                            <div className="font-semibold line-clamp-1 text-slate-800">{ln.title}</div>
                            <div className="flex items-center justify-between text-[10px] text-slate-500 mt-0.5">
                                <span className="font-mono">{ln.ean}</span>
                                <span className="font-bold text-emerald-700 text-xs">×{ln.quantity}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex gap-1">
                <button onClick={onManual}
                    className="flex-1 h-7 text-[11px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 flex items-center justify-center gap-1"
                    data-testid={`k-manual-pick-${c.order_id}`}>
                    <CheckCircle2 size={12} /> Hazırlandı
                </button>
                <button onClick={onReprint}
                    className="h-7 px-2 text-[11px] font-bold bg-slate-200 text-slate-700 rounded hover:bg-slate-300 flex items-center gap-1"
                    title="Etiketi tekrar bas"
                    data-testid={`k-reprint-${c.order_id}`}>
                    <Printer size={12} />
                </button>
            </div>
        </div>
    );
}
